import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import { FC } from "react";
import { rangeToString } from "src/utils/range";
import { rebaScoresDetail } from "./rebaScoresDetail";
import { rulaScoresDetail } from "./rulaScoresDetail";
import { eraScoresDetail } from "./eraScoresDetail";
import { MsdScoreDetailType, msdScoresDetail } from "./msdScoresDetail";
import { AssessmentRo } from "src/app/services/generatedApi";

const ScoreDetailRow: FC<{
  item: MsdScoreDetailType;
}> = ({ item }) => {
  const IconComponent = item.icon;
  return (
    <TableRow sx={{ background: item.color }}>
      <TableCell>
        <IconComponent />
      </TableCell>
      <TableCell align="center">
        {rangeToString(item.range.min, item.range.max)}
      </TableCell>
      <TableCell>{item.detail}</TableCell>
    </TableRow>
  );
};

type HelpDialogPropsType = {
  open: boolean;
  handleClose: () => void;
  method: AssessmentRo["method"];
};

export const HelpDialog: FC<HelpDialogPropsType> = ({
  open,
  handleClose,
  method,
}) => {
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{method}</DialogTitle>
      <DialogContent>
        <DialogContentText mb={2}>
          {method === "REBA"
            ? "The Rapid Entire Body Assessment (REBA) is a simple observational assessment tool used by ergonomists and other safety professionals to evaluate the physical stress and discomfort that workers may experience during their job tasks. It helps to identify potential ergonomic issues in the workplace early on before they can cause costly and painful musculoskeletal disorders by scoring the postures observed by the worker during the task. REBA consists of a series of steps that involve observing the worker's posture, then determining and assigning scores to different body parts based on their positioning and degree of stress. The end-result provides an indication of the overall level of ergonomic risk associated with the task as calculated by combining the individual segment scores."
            : method === "RULA"
              ? "The Rapid Upper Limb Assessment (RULA) is a diagnostic tool used to assess the ergonomic risk levels associated with upper limb disorders resulting from repetitive strain and muscular discomfort in the workplace. Developed to evaluate the posture of the neck, trunk, and upper limbs along with muscle function and external loads, RULA uses a systematic process to grade the risk of injury based on the observation of static and dynamic positions of the body during work tasks. The assessment involves scoring various body regions including the arms, wrists, neck, and trunk, with higher scores indicating greater risk of musculoskeletal injury. The final score is used to determine the level of action required to reduce risk, ranging from no action to immediate investigation and changes in the workplace. RULA is widely utilized in occupational health and ergonomics to help identify work activities that pose increased risk for upper limb disorders, promoting early intervention and the redesign of work tasks to improve posture and reduce muscular strain."
              : method === "MMH"
                ? "The Liberty Mutual manual materials handling equations are a set of guidelines that help to evaluate the risk of injury associated with manually lifting, lowering, carrying, pushing, or pulling materials in the workplace. These guidelines are based on biomechanical models that consider various factors, such as frequency the weight of the object, the horizontal distance between the worker and the object, the vertical distance of the lift, and the posture of the worker. Based on these factors, the equations provide recommendations for safe manual material handling guidelines, including the maximum weight that should be lifted, lowered, carried, pushed, or pulled, the percent of population capable and the appropriate grip and posture. The purpose of these equations is to help prevent musculoskeletal injuries, such as strains and sprains, that can occur from repetitive manual lifting tasks."
                : method === "ERA"
                  ? "The Ergonomic Risk Assessment (ERA) is a cyclic job analysis tool that focuses on the upper body. It evaluates risk factors such as posture, force, repetition, and static exertion during work tasks. Using video samples, ERA helps to identify ergonomic risks and provides a scoring system to categorize risk levels. The assessment aids in determining whether further investigation or immediate corrective actions are required to reduce musculoskeletal disorder risks."
                  : "The MSD (Musculoskeletal Disorder) Screening Tool employs Boolean logical operators within a hierarchical threshold-based evaluation matrix to quantify biomechanical stressors across anatomical segments. The assessment utilizes dichotomous classification (Caution/Hazard) with time-dependent exposure thresholds to derive cumulative risk indices. This methodology evaluates risk factors for different body parts, including the low back, hand/wrist, neck/shoulder, and knee regions, by measuring exposure duration and force application to identify potential ergonomic hazards."}
        </DialogContentText>
        {(method === "REBA" ||
          method === "RULA" ||
          method === "ERA" ||
          method === "MSD") && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ background: "#ddd", "& th": { fontWeight: 600 } }}
                >
                  <TableCell></TableCell>
                  <TableCell align="center">Score/Level</TableCell>
                  <TableCell>Level of MSD Risk</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(method === "REBA"
                  ? rebaScoresDetail
                  : method === "RULA"
                    ? rulaScoresDetail
                    : method === "ERA"
                      ? eraScoresDetail
                      : msdScoresDetail
                ).map((item, index) => (
                  <ScoreDetailRow item={item} key={index} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
};
